import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import { Switch } from '@headlessui/react';
import { clsx } from 'clsx';
import {
  Button, Modal, bjToast, TailwindSpinner,
} from '@components/common';
import { Textarea } from '@components/common/Forms';
import { BulkOperationProps } from '@type/bulk-operations';
import {
  useSwrBulkUnlock,
  useSwrBulkRequestInterviews,
} from '@hooks/useSwrApi/endpoints/bulk-operations';
import { useSwrJobBasicInfo } from '@hooks/useSwrApi/endpoints/talent-search';
import { fetcher } from '@utils/data-fetching';
import { MAX_ENABLED_INTERVIEW_QUESTIONS } from 'src/constants';

interface BulkValidateProps extends BulkOperationProps {
  /**
   * Slugs of currently-selected applicants that are still locked. The parent
   * (ApplicantListContent) derives this from the applicantList + form values —
   * we don't compute it here because we don't have direct access to the list.
   */
  getLockedApplicantIds?: () => string[];
  hideTriggerIcon?: boolean;
  triggerDisabled?: boolean;
  maxBatch?: number;
}

interface PriceResponse {
  price?: number;
  fullPrice?: number;
}

/**
 * Interview-request contract: the job needs at least one enabled screening
 * question; the max matches the job-edit form
 * (`MAX_ENABLED_INTERVIEW_QUESTIONS` from `src/constants`). Reusing the same
 * cap keeps the two flows compatible — a job configured on job-edit with 5
 * enabled questions is not silently truncated when the recruiter enters the
 * bulk-validate flow.
 */
const MIN_INTERVIEW_QUESTIONS = 1;

/** Row shape used by the modal — mirrors `ApiSchema<'InterviewQuestion'>`. */
interface InterviewQuestionRow {
  id?: string;
  question: string;
  enabled: boolean;
  /**
   * Backend flag: true when the question already has answers from
   * previous applicants. The row stays visible but the recruiter can
   * neither edit the text nor delete it — only toggle it off. Same
   * semantics as the job-edit form.
   */
  readOnly: boolean;
}

interface JobTemplateResponse {
  interviewQuestions?: {
    id?: string;
    question?: string;
    enabled?: boolean;
    readOnly?: boolean;
  }[];
}

const toRow = (raw: {
  id?: string;
  question?: string;
  enabled?: boolean;
  readOnly?: boolean;
}): InterviewQuestionRow => ({
  id: raw.id,
  question: String(raw.question ?? ''),
  enabled: raw.enabled !== false,
  readOnly: raw.readOnly === true,
});

/**
 * @description Bulk-validate = "Interviewváz" from the applicants list.
 *
 * Flow (mirrors the per-CV detail flow):
 *   1. Get selected slugs. Empty → toast + bail. > maxBatch → toast + bail.
 *   2. If the job has no active interview questions (`miniInterview === false`),
 *      generate 3 questions from the job title/description and let the recruiter
 *      review/edit them, then save them to the job.
 *   3. Sum unlock prices for the currently-locked selected applicants
 *      (already-unlocked ones contribute 0 — we don't hit the price endpoint
 *      for them). Show a confirmation modal with the total.
 *   4. On confirm: bulk-unlock locked slugs (if any), then bulk-request-interviews
 *      for ALL selected slugs. Success toast with per-bucket counts.
 *
 * Uses `fetcher` directly for the three on-demand calls (generate, save,
 * price) — bespoke SWR hooks for one-shot mutations are overhead here.
 */
export const BulkValidate = (props: BulkValidateProps) => {
  const {
    getSelectedApplicants,
    jobSlug = '',
    locale = i18nConfig.defaultLocale,
    normalizeSelectedApplicantsForBulkAction,
    getLockedApplicantIds,
    hideTriggerIcon = false,
    triggerDisabled = false,
    maxBatch = 100,
  } = props;

  const { t } = useTranslation('common');

  // Job basic info (miniInterview flag + jobTitle for question generation)
  const { data: jobBasicInfo } = useSwrJobBasicInfo(jobSlug, locale);

  // Modal state
  const [isQuestionsOpen, setIsQuestionsOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [questions, setQuestions] = useState<InterviewQuestionRow[]>([]);
  const [limitMessage, setLimitMessage] = useState<string>('');

  // Pricing state
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [lockedCount, setLockedCount] = useState<number>(0);
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSavingQuestions, setIsSavingQuestions] = useState(false);

  // Refs so the phases can chain without prop drilling through modal state.
  const selectedSlugsRef = useRef<string[]>([]);
  const lockedSlugsRef = useRef<string[]>([]);

  const normalize = useCallback((raw: string[]) => (
    normalizeSelectedApplicantsForBulkAction ? normalizeSelectedApplicantsForBulkAction(raw) : raw
  ), [normalizeSelectedApplicantsForBulkAction]);

  const { trigger: triggerBulkRequestInterviews, isMutating: isRequestingInterviews } = useSwrBulkRequestInterviews({
    jobSlug,
    locale,
    // Own the messaging — the auto success toast would fire alongside our
    // per-bucket summary (and even on all-skipped 200s, which is why the
    // user was seeing "Nothing to validate" + "Operatiunea a fost realizată
    // cu success" back-to-back).
    showSuccessToast: false,
    onSuccess: (response) => {
      const succeeded = response?.succeeded ?? 0;
      const skipped = response?.skipped ?? 0;
      const insufficient = response?.insufficientBalance ?? 0;

      if (insufficient > 0) {
        bjToast.error(t('cv.action.bulk.validate.insufficient_balance', { count: insufficient }));
      }
      if (succeeded > 0) {
        bjToast.success(t('cv.action.bulk.validate.success', {
          count: succeeded,
          defaultValue: '{{count}} interview requests sent',
        }));
        return;
      }
      if (skipped > 0) {
        // 0 succeeded + N skipped = the whole selection was already-validated
        // or otherwise ineligible. Report it as info-level, no green tick.
        bjToast(t('cv.action.bulk.validate.all_skipped', {
          defaultValue: 'Nothing to validate — selection has no eligible applicants',
        }));
      }
    },
  });

  const { trigger: triggerBulkUnlock, isMutating: isUnlocking } = useSwrBulkUnlock({
    jobSlug,
    locale,
    onSuccess: () => {
      void triggerBulkRequestInterviews({ ids: selectedSlugsRef.current });
    },
  });

  const isBusy = isFetchingPrices
    || isGenerating
    || isSavingQuestions
    || isUnlocking
    || isRequestingInterviews;

  const closeQuestionsModal = useCallback(() => {
    setIsQuestionsOpen(false);
    setQuestions([]);
    setLimitMessage('');
  }, []);

  const closePricingModal = useCallback(() => {
    setIsPricingOpen(false);
    setTotalPrice(0);
    setLockedCount(0);
  }, []);

  // Step 3: always open the confirmation modal so the recruiter can review
  // the total cost (even 0) and the breakdown before firing anything. Only
  // fetches per-slug prices when there are locked applicants — already-
  // unlocked ones contribute 0 without a network call.
  const showPricingOrRun = useCallback(async () => {
    const lockedSlugs = lockedSlugsRef.current;
    setLockedCount(lockedSlugs.length);
    setIsPricingOpen(true);

    if (lockedSlugs.length === 0) {
      setTotalPrice(0);
      return;
    }

    setIsFetchingPrices(true);
    // Per-slug price. `jobSlug` MUST be sent so the endpoint applies the
    // freemium-job unlock multiplier — omitting it returns the bare
    // DEFAULT_PRICE and would understate the real charge for hire+/freemium
    // bundles.
    try {
      const priceResults = await Promise.all(
        lockedSlugs.map((slug) => (
          fetcher<PriceResponse>(
            `/v1/resume-operation/${slug}/price?jobSlug=${encodeURIComponent(jobSlug)}`,
            locale,
            { method: 'GET' },
          ).catch((): PriceResponse => ({ price: 0 }))
        )),
      );

      const sum = priceResults.reduce((acc, r) => acc + (r?.price ?? 0), 0);
      setTotalPrice(sum);
    } finally {
      setIsFetchingPrices(false);
    }
  }, [jobSlug, locale]);

  // Step 2b: save questions to the job then move to pricing.
  //
  // The /save endpoint accepts `{ questions: string[] }` — the recruiter's
  // enabled, non-empty question texts. Disabled or empty rows are dropped;
  // read-only rows that stay enabled ride through by their text (the
  // backend re-matches by text/order to preserve answer correlation).
  const handleSaveQuestions = useCallback(async () => {
    const enabledClean = questions
      .filter((q) => q.enabled)
      .map((q) => q.question.trim())
      .filter((q) => q.length > 0);

    if (enabledClean.length < MIN_INTERVIEW_QUESTIONS) {
      bjToast.error(t('cv.action.bulk.validate.questions.min', {
        count: MIN_INTERVIEW_QUESTIONS,
        defaultValue: 'Enable at least {{count}} interview question',
      }));
      return;
    }
    if (enabledClean.length > MAX_ENABLED_INTERVIEW_QUESTIONS) {
      bjToast.error(t('cv.action.bulk.validate.questions.max', {
        count: MAX_ENABLED_INTERVIEW_QUESTIONS,
        defaultValue: 'You can enable at most {{count}} interview questions',
      }));
      return;
    }

    setIsSavingQuestions(true);
    try {
      await fetcher<undefined>(
        `/v1/interview-questions/${jobSlug}/save`,
        locale,
        {
          method: 'POST',
          payload: { questions: enabledClean },
          stringifyPayload: true,
          headers: { 'Content-Type': 'application/json' },
        },
      );
      closeQuestionsModal();
      await showPricingOrRun();
    } catch {
      bjToast.error(t('cv.action.bulk.validate.questions.save_error', {
        defaultValue: 'Could not save interview questions. Please try again.',
      }));
    } finally {
      setIsSavingQuestions(false);
    }
  }, [questions, jobSlug, locale, t, closeQuestionsModal, showPricingOrRun]);

  // Step 2a: always open the questions modal. Preload with existing job
  // questions (full objects — id, enabled, readOnly) so we can reuse the
  // job-edit gating: readOnly rows keep their text/answers but the recruiter
  // can still toggle them off; enable-cap and delete-guard mirror job-edit.
  // Falls back to a fresh generation when the job has no questions.
  //
  // The generate call sends `description` as an empty string (never omitted)
  // — the endpoint crashes when the key is missing.
  const openQuestionsFlow = useCallback(async () => {
    if (!jobSlug) {
      bjToast.error(t('cv.action.bulk.validate.questions.generate_error', {
        defaultValue: 'Could not generate interview questions. Please try again.',
      }));
      return;
    }
    setIsGenerating(true);
    try {
      // 1) Load existing questions from the job template.
      const template = await fetcher<JobTemplateResponse>(
        `/v1/job-operation/job-template?jobSlug=${encodeURIComponent(jobSlug)}`,
        locale,
        { method: 'GET' },
      ).catch((): JobTemplateResponse => ({}));

      const existingRows = (template?.interviewQuestions ?? [])
        .map(toRow)
        .filter((row) => row.question.length > 0);

      if (existingRows.length > 0) {
        setQuestions(existingRows);
        setLimitMessage('');
        setIsQuestionsOpen(true);
        return;
      }

      // 2) No existing questions — generate a fresh set. Always send
      //    description as a string (empty is fine); omitting it crashes
      //    the generator.
      const response = await fetcher<{ questions?: string[] }>(
        '/v1/interview-questions/generate',
        locale,
        {
          method: 'POST',
          payload: {
            title: String(jobBasicInfo?.title ?? ''),
            description: '',
          },
          stringifyPayload: true,
          headers: { 'Content-Type': 'application/json' },
        },
      );
      const generatedTexts = Array.from(new Set(
        (response?.questions ?? []).map((q) => q.trim()).filter(Boolean),
      )).slice(0, MAX_ENABLED_INTERVIEW_QUESTIONS);

      const rows: InterviewQuestionRow[] = generatedTexts.length > 0
        ? generatedTexts.map((question) => ({
          question, enabled: true, readOnly: false,
        }))
        : [{ question: '', enabled: true, readOnly: false }];

      setQuestions(rows);
      setLimitMessage('');
      setIsQuestionsOpen(true);
    } catch {
      bjToast.error(t('cv.action.bulk.validate.questions.generate_error', {
        defaultValue: 'Could not generate interview questions. Please try again.',
      }));
    } finally {
      setIsGenerating(false);
    }
  }, [jobSlug, jobBasicInfo?.title, locale, t]);

  // Entry point.
  const handleClick = useCallback(async () => {
    const raw = getSelectedApplicants();
    const normalized = normalize(raw);
    // Fall back to raw if normalization returns nothing — protects against
    // list/checkbox drift where a valid selected slug isn't yet marked as
    // bulk-actionable by the mapper (e.g. newly-added applicants that haven't
    // been reindexed yet).
    const slugs = normalized.length > 0 ? normalized : raw;

    if (slugs.length === 0) {
      bjToast.error(t('cv.action.bulk.select_cv'));
      return;
    }
    if (slugs.length > maxBatch) {
      bjToast.error(t('cv.action.bulk.validate.cap_exceeded', {
        max: maxBatch,
        defaultValue: 'Please select at most {{max}} applicants',
      }));
      return;
    }

    selectedSlugsRef.current = slugs;
    lockedSlugsRef.current = getLockedApplicantIds
      ? getLockedApplicantIds().filter((s) => slugs.includes(s))
      : [];

    // Always open the questions modal — even when the job already has
    // enabled questions. openQuestionsFlow loads existing ones from the
    // job template so the recruiter reviews the actual list before
    // committing to the interview batch. Only when the job has no
    // questions does the flow fall back to auto-generation.
    await openQuestionsFlow();
  }, [
    getSelectedApplicants,
    getLockedApplicantIds,
    normalize,
    maxBatch,
    openQuestionsFlow,
    t,
  ]);

  const handlePricingConfirm = useCallback(() => {
    setIsPricingOpen(false);
    if (lockedSlugsRef.current.length > 0) {
      // triggerBulkRequestInterviews chained inside triggerBulkUnlock.onSuccess.
      void triggerBulkUnlock({ ids: lockedSlugsRef.current });
    } else {
      void triggerBulkRequestInterviews({ ids: selectedSlugsRef.current });
    }
  }, [triggerBulkUnlock, triggerBulkRequestInterviews]);

  const enabledCount = questions.filter((q) => q.enabled).length;
  const canAddQuestion = enabledCount < MAX_ENABLED_INTERVIEW_QUESTIONS;

  const addQuestion = () => {
    setQuestions((prev) => {
      const nextEnabled = prev.filter((q) => q.enabled).length + 1;
      if (nextEnabled > MAX_ENABLED_INTERVIEW_QUESTIONS) {
        setLimitMessage(t('cv.action.bulk.validate.questions.max', {
          count: MAX_ENABLED_INTERVIEW_QUESTIONS,
          defaultValue: 'You can enable at most {{count}} interview questions',
        }));
        return prev;
      }
      setLimitMessage('');
      return [...prev, { question: '', enabled: true, readOnly: false }];
    });
  };

  const updateQuestion = (index: number, value: string) => {
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== index) return q;
      // Guard: readOnly rows are protected even if the DOM allows a keypress.
      if (q.readOnly) return q;
      return { ...q, question: value };
    }));
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => {
      // Guard: never remove the last row (leaves the recruiter with an
      // empty list). Mirrors job-edit's `showDeleteButton` check.
      if (prev.length <= 1) return prev;
      // Guard: readOnly rows can't be deleted.
      if (prev[index]?.readOnly) return prev;
      setLimitMessage('');
      return prev.filter((_, i) => i !== index);
    });
  };

  const toggleQuestionEnabled = (index: number, checked: boolean) => {
    setQuestions((prev) => {
      const list = [...prev];
      const row = list[index];
      if (!row) return prev;

      if (checked) {
        const currentEnabled = list.filter((q) => q.enabled).length;
        if (currentEnabled >= MAX_ENABLED_INTERVIEW_QUESTIONS) {
          setLimitMessage(t('cv.action.bulk.validate.questions.max', {
            count: MAX_ENABLED_INTERVIEW_QUESTIONS,
            defaultValue: 'You can enable at most {{count}} interview questions',
          }));
          return prev;
        }
      }

      setLimitMessage('');
      list[index] = { ...row, enabled: checked };
      return list;
    });
  };

  const summaryLine = useMemo(() => {
    const total = selectedSlugsRef.current.length;
    const unlockedCount = Math.max(0, total - lockedCount);
    return t('cv.action.bulk.validate.summary', {
      count: total,
      lockedCount,
      unlockedCount,
      defaultValue: '{{count}} selected · {{unlockedCount}} already unlocked · {{lockedCount}} to unlock',
    });
  }, [t, lockedCount]);

  return (
    <>
      <Button
        styling="text"
        size="sm"
        color="ink"
        className="grow px-4"
        aria-label={t('cv.action.bulk.validate', 'Interview')}
        isLoading={isBusy && !isQuestionsOpen && !isPricingOpen}
        disabled={triggerDisabled || isBusy}
        onClick={() => { void handleClick(); }}
      >
        {!isBusy && !hideTriggerIcon && <CheckCircleIcon className="mr-2 size-5 md:mr-1" />}
        <span className={hideTriggerIcon ? undefined : 'lg:pl-px'}>{t('cv.action.bulk.validate', 'Interview')}</span>
      </Button>

      {/* Interview-questions review modal — only shown when the job has no
          enabled questions yet. Save persists to /v1/interview-questions/{jobSlug}/save
          then flows into the pricing modal. */}
      <Modal isOpen={isQuestionsOpen} onClose={closeQuestionsModal} size="md" centered>
        <Modal.Header onClose={closeQuestionsModal}>
          <h3 className="text-lg font-bold text-ink">
            {t('cv.action.bulk.validate.questions.title', 'Interview questions')}
          </h3>
        </Modal.Header>
        <Modal.Body className="bg-surface-100">
          <p className="mb-4 text-sm text-ink-medium">
            {t('cv.action.bulk.validate.questions.description', 'These questions will be sent to every candidate you interview. You can edit them before saving.')}
          </p>
          <ul className="space-y-3">
            {questions.map((row, index) => {
              const canDelete = questions.length > 1 && !row.readOnly;
              return (
                <li key={`q-${row.id ?? index}`} className="flex items-start gap-2">
                  <span className="mt-2 shrink-0 text-sm font-semibold text-ink">{index + 1}.</span>
                  <Textarea
                    value={row.question}
                    rows={2}
                    readOnly={row.readOnly}
                    aria-readonly={row.readOnly}
                    onChange={(e) => updateQuestion(index, e.target.value)}
                    className="grow"
                  />
                  {/* Enable / disable toggle — same behaviour as job-edit:
                      readOnly rows CAN still be toggled off, they just can't
                      be edited or deleted (their answers stay tied to the id). */}
                  <Switch
                    checked={row.enabled}
                    onChange={(value) => toggleQuestionEnabled(index, value)}
                    className={clsx(
                      'relative mt-1 inline-flex h-6 w-12 shrink-0 items-center rounded-full transition',
                      row.enabled ? 'bg-secondary' : 'bg-input',
                    )}
                    aria-label={row.enabled ? t('global.action.disable') : t('global.action.enable')}
                    title={row.enabled ? t('global.action.disable') : t('global.action.enable')}
                  >
                    <span
                      className={clsx(
                        'inline-block size-4 rounded-full bg-surface transition',
                        row.enabled ? 'translate-x-6' : 'translate-x-1',
                      )}
                    />
                  </Switch>
                  {canDelete ? (
                    <Button
                      type="button"
                      styling="outline"
                      size="sm"
                      color="light"
                      className="!p-2"
                      onClick={() => removeQuestion(index)}
                      aria-label={t('global.action.delete')}
                      title={t('global.action.delete')}
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  ) : (
                    <span className="w-8 shrink-0" aria-hidden />
                  )}
                </li>
              );
            })}
          </ul>
          {canAddQuestion && (
            <Button
              type="button"
              styling="outline"
              size="sm"
              color="light"
              className="mt-4"
              onClick={addQuestion}
              title={t('cv.action.bulk.validate.questions.add', 'Add question')}
            >
              <PlusIcon className="mr-1 size-4" />
              {`${t('cv.action.bulk.validate.questions.add', 'Add question')} (${enabledCount}/${MAX_ENABLED_INTERVIEW_QUESTIONS})`}
            </Button>
          )}
          {limitMessage && (
            <p className="mt-2 text-sm text-error">{limitMessage}</p>
          )}
          <p className="mt-2 text-xs text-ink-light">
            {t('cv.action.bulk.validate.questions.limits', {
              min: MIN_INTERVIEW_QUESTIONS,
              max: MAX_ENABLED_INTERVIEW_QUESTIONS,
              defaultValue: 'Enable between {{min}} and {{max}} questions.',
            })}
          </p>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2">
          <Button
            type="button"
            color="light"
            styling="outline"
            rounding="full"
            size="sm"
            onClick={closeQuestionsModal}
            disabled={isSavingQuestions}
          >
            {t('global.action.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            color="secondary"
            rounding="full"
            size="sm"
            onClick={() => { void handleSaveQuestions(); }}
            isLoading={isSavingQuestions}
            disabled={isSavingQuestions}
          >
            {t('cv.action.bulk.validate.questions.save_and_continue', 'Save and continue')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pricing confirmation modal — total unlock cost across the currently-locked
          slugs. Confirm chains bulk-unlock → bulk-request-interviews. */}
      <Modal isOpen={isPricingOpen} onClose={closePricingModal} size="sm" centered>
        <Modal.Header onClose={closePricingModal}>
          <h3 className="text-lg font-bold text-ink">
            {t('cv.action.bulk.validate.pricing.title', 'Confirm interview cost')}
          </h3>
        </Modal.Header>
        <Modal.Body className="bg-surface-100">
          <p className="mb-3 text-sm text-ink-medium">{summaryLine}</p>
          {isFetchingPrices ? (
            <div className="flex items-center justify-center py-6">
              <TailwindSpinner className="size-5" />
            </div>
          ) : (
            <div className="flex items-end justify-center gap-1 py-4">
              <span className="text-sm text-ink-medium">{t('cv.detail.contact.label')}:</span>
              <span className="text-[30px] font-bold leading-none text-secondary">{totalPrice}</span>
              <span className="text-base font-medium text-secondary">{t('global.label.credits')}</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2">
          <Button
            type="button"
            color="light"
            styling="outline"
            rounding="full"
            size="sm"
            onClick={closePricingModal}
            disabled={isFetchingPrices}
          >
            {t('global.label.unlock.candidate.deny', 'Cancel')}
          </Button>
          <Button
            type="button"
            color="secondary"
            rounding="full"
            size="sm"
            onClick={handlePricingConfirm}
            isLoading={isUnlocking || isRequestingInterviews}
            disabled={isFetchingPrices || isUnlocking || isRequestingInterviews}
          >
            {t('global.label.unlock.candidate.info', 'Confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
